import {
  subscribeTopic as AmitySubscribeTopic,
  CommentRepository,
  FileRepository,
} from "@amityco/ts-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AdminTag } from "@/components";
import { ThreadIcon } from "@/icons";
import { useAmityAuth } from "@/providers/context";
import { cn, formatRelativeDateForCommunity } from "@/utils";

import {
  CommentButton,
  CommunityCommentDropdown,
  CommunityCommentInput,
  CommunityUserAvatar,
  FlagCount,
  LikeButton,
} from "../..";
import { CommentItem } from "../../../types";
import { isCommunityAdmin, mapAmityComments } from "../../../util";

const MAX_LAYER = 3;

export const CommunityComment = ({
  comment,
  layer,
  isExpanded,
  onToggleExpanded,
}: {
  comment: CommentItem;
  layer: number;
  isExpanded?: boolean;
  onToggleExpanded: () => void;
}) => {
  const {
    id,
    subscribeTopic,
    commentCreator,
    date,
    textContent,
    attachments,
    haveLiked,
    likes,
    repliesCount,
  } = comment;

  const { authState } = useAmityAuth();
  const isAdmin = isCommunityAdmin(authState?.authenticatedUser);

  const [replies, setReplies] = useState<CommentItem[]>([]);
  const [childExpandedComments, setChildExpandedComments] = useState<
    Set<string>
  >(new Set());

  const processedAttachments = useMemo(() => {
    if (!attachments || attachments.length === 0) {
      return [];
    }

    return attachments.map((attachment) => {
      const fullSizeUrl = FileRepository.fileUrlWithSize(
        attachment.fileUrl,
        "full",
      );
      const aspectRatio =
        attachment.attributes.metadata.width /
        attachment.attributes.metadata.height;

      return {
        ...attachment,
        fullSizeUrl,
        aspectRatio,
      };
    });
  }, [attachments]);

  useEffect(() => {
    const unsubscribe = AmitySubscribeTopic(subscribeTopic);

    return unsubscribe;
  }, [subscribeTopic]);

  // Clear child expanded comments when parent is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setChildExpandedComments(new Set());
    }
  }, [isExpanded]);

  const handleGetReplies = useCallback<
    Amity.LiveCollectionCallback<Amity.InternalComment>
  >(
    async ({ data }) => {
      if (data) {
        const mappedReplies = await mapAmityComments({
          comments: data,
          currentUserId: authState.authenticatedUser?.userId,
        });
        setReplies(mappedReplies.reverse());
      }
    },
    [authState.authenticatedUser?.userId, setReplies],
  );
  useEffect(() => {
    if (layer < MAX_LAYER) {
      const getRepliesParams: Amity.CommentLiveCollection = {
        referenceType: "post",
        referenceId: comment.referenceId,
        dataTypes: { values: ["text", "image"], matchType: "any" },
        sortBy: "lastCreated",
        parentId: comment.id,
        limit: 30,
      };
      const unsubscribe = CommentRepository.getComments(
        getRepliesParams,
        handleGetReplies,
      );
      return unsubscribe;
    }
  }, [comment.id, comment.referenceId, layer, handleGetReplies]);

  const handleToggleChildExpanded = useCallback((commentId: string) => {
    setChildExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);

  return (
    <div className={layer === 1 ? "mb-10" : ""}>
      {layer === 1 && (
        <div className="ml-3 h-[1px] w-[calc(100%-12px)] bg-border" />
      )}
      <div
        className={cn(
          "relative flex flex-col gap-6",
          layer === 1 ? "pt-10" : "pb-9",
        )}
      >
        {layer !== 1 && (
          <div className="absolute left-[-18px] top-[3px] text-border">
            <ThreadIcon className="h-[20px] w-[16px]" />
          </div>
        )}
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-center gap-4">
            <CommunityUserAvatar user={commentCreator} size="s" />
            <div className="w-full overflow-hidden">
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 overflow-hidden">
                  <div className="p2-semibold ellipsis">
                    {commentCreator.displayName}
                  </div>
                  {isCommunityAdmin(commentCreator) && <AdminTag />}
                </div>
                <CommunityCommentDropdown comment={comment} />
              </div>
              <div className="flex h-5 items-center gap-2">
                <div className="caption-normal text-item-secondary">
                  {formatRelativeDateForCommunity(date)}
                </div>
                {isAdmin && comment.flagCount > 0 && (
                  <FlagCount value={comment.flagCount} />
                )}
              </div>
            </div>
          </div>
          <div className="p2-regular flex flex-col gap-5 whitespace-pre-wrap pl-1">
            {textContent}
          </div>
          {processedAttachments.length > 0 && (
            <div className="flex flex-col gap-4">
              {processedAttachments.map((attachment) => (
                <div
                  key={attachment.fileId}
                  className="relative w-full"
                  style={{
                    paddingBottom: `${(1 / attachment.aspectRatio) * 100}%`,
                  }}
                >
                  <div className="absolute inset-0 animate-pulse rounded-xl bg-surface-tertiary" />
                  <img
                    src={attachment.fullSizeUrl}
                    alt={attachment.type}
                    className="absolute inset-0 h-full w-full rounded-xl object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 pl-2">
          <LikeButton
            item={{
              id,
              type: "comment",
              textContent: textContent ?? "",
              userId: commentCreator.userId,
            }}
            haveLiked={haveLiked}
            likesCount={likes}
            type={"comment"}
          />
          {layer < MAX_LAYER && (
            <CommentButton value={repliesCount} onClick={onToggleExpanded} />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="ml-7 pb-9">
          <div
            className={cn(
              "border-l-[2px] border-border pl-4",
              layer === 1 ? "mt-9" : "mt-0",
            )}
          >
            {replies.map((reply) => (
              <CommunityComment
                key={reply.id}
                comment={reply}
                layer={layer + 1}
                isExpanded={childExpandedComments.has(reply.id)}
                onToggleExpanded={() => handleToggleChildExpanded(reply.id)}
              />
            ))}
          </div>
          <div className="relative pl-[34px]">
            <CommunityCommentInput
              parentId={comment.id}
              referenceId={comment.referenceId}
              replyingTo={comment.commentCreator.displayName}
            />
            <div className="absolute left-0 top-0 h-1 border-l-[2px] border-border" />
            <div className="absolute left-0 top-[3px] text-border">
              <ThreadIcon className="h-[20px] w-[16px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
